import React, { useState, useEffect } from 'react';
import { Layout, Menu, Card, Typography, Row, Col, message } from 'antd';
import { BookOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import styles from './Study.module.css';

const { Title, Text } = Typography;
const { Sider, Content } = Layout;

const categoryDescriptions = {
  accounting: `紧扣《会计准则》和税法，分模块强化分录与报表实务；结合真题训练计算速度，关注政策变动；模拟限时综合题提升应试精准度。`,
  computer: `按大纲分级突破，主攻算法、数据库及案例分析；通过项目实操编程逻辑，用错题本总结高频陷阱；考前聚焦历年考点突击。`,
  construction: `熟记法规条文与施工流程，推导工程量公式逻辑；案例结合图纸分析，关键词记忆考点；模拟卷严格计时，避免超时。`,
  economics: `吃透宏观/微观模型，熟练统计公式推导；关联时事理解理论，图表对比易混概念；强化计算题与数学模型应用。`,
  teacher: `夯实教育理论与学科知识，设计教案模板与案例分析；主观题按"观点+理论+实例"结构作答；模拟试讲并自评改进表达细节。`
};

const Study = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subCategories, setSubCategories] = useState({});
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  // 获取分类数据
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/categories');
      if (response.data.success) {
        const { mainCategories, subCategories } = response.data;
        setCategories(mainCategories);
        setSubCategories(subCategories);
        // 默认选中会计专业
        const accountingCategory = mainCategories.find(cat => cat.code === 'accounting');
        if (accountingCategory) {
          setSelectedCategory(accountingCategory);
        }
        const lastSelected = localStorage.getItem('lastSelectedCategory');
        let defaultCategory;
        if (lastSelected) {
          defaultCategory = JSON.parse(lastSelected)
          navigate(`/study/book/${defaultCategory.subCategory}`, {
            state: {
              categoryName: defaultCategory.mainCategoryName,
              subCategoryName: defaultCategory.subCategoryName,
              categoryCode: defaultCategory.mainCategory,
              subCategoryCode: defaultCategory.subCategory
            }
          })
        }
      }
    } catch (error) {
      console.error('获取分类失败:', error);
      message.error('获取分类数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理分类点击
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setBooks([]); 
  };

  // 修改卡片点击处理函数
  const handleSubCategoryClick = (subCategory) => {
    console.log('Navigating to:', subCategory);
    navigate(`/study/book/${subCategory.code}`, {
      state: {
        categoryName: selectedCategory.name,
        subCategoryName: subCategory.name,
        categoryCode: selectedCategory.code,
        subCategoryCode: subCategory.code
      }
    });
  };

  // 渲染左侧菜单项
  const menuItems = categories.map(category => ({
    key: category.code,
    icon: <BookOutlined />,
    label: category.name,
  }));

  return (
    <div className={styles.studyContainer}>
      {/* 替换原有的文字描述为 SVG */}
      <div className={styles.pageDescription}>
        <svg width="1152" height="183" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{ stopColor: '#141414', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#141414', stopOpacity: 1 }} />
            </linearGradient>
            <linearGradient id="cardGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#1f1f1f', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#1f1f1f', stopOpacity: 1 }} />
            </linearGradient>
            <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#3fc1c9', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#2ecc71', stopOpacity: 1 }} />
            </linearGradient>
          </defs>

          <rect width="1152" height="133" fill="url(#bgGradient)"/>
          
          <path d="M0 20 h1152 M0 60 h1152 M0 100 h1152" 
                stroke="#303030" 
                strokeWidth="0.5" 
                strokeDasharray="2,8"/>
          
          <g transform="translate(40, 15)">
            <rect x="130" y="20" width="85" height="105" 
                  fill="url(#cardGradient)" 
                  stroke="#4a90e2" 
                  strokeWidth="1.5" 
                  rx="8" 
                  opacity="0.6"/>
            <rect x="65" y="10" width="85" height="105" 
                  fill="url(#cardGradient)" 
                  stroke="#ff6b6b" 
                  strokeWidth="1.5" 
                  rx="8" 
                  opacity="0.8"/>
            <rect x="0" y="0" width="85" height="105" 
                  fill="url(#cardGradient)" 
                  stroke="#2ecc71" 
                  strokeWidth="1.5" 
                  rx="8" 
                  filter="drop-shadow(0 2px 3px rgba(0,0,0,0.1))"/>
            <text x="42" y="30" textAnchor="middle" fill="#ffffff" fontSize="12" fontWeight="500">核心概念</text>
            <line x1="15" y1="45" x2="70" y2="45" stroke="#333"/>
            <text x="42" y="65" textAnchor="middle" fill="#999" fontSize="10">记忆要点</text>
            <circle cx="42" cy="85" r="12" fill="#1f1f1f" stroke="#2ecc71" strokeWidth="1.5"/>
            <text x="42" y="89" textAnchor="middle" fill="#2ecc71" fontSize="12">✓</text>
          </g>
          
          <path d="M 250 100 Q 350 20, 450 50 T 650 70 T 850 60 T 1050 65" 
                fill="none" 
                stroke="url(#progressGradient)" 
                strokeWidth="2.5"
                filter="drop-shadow(0 2px 2px rgba(0,0,0,0.1))"/>
          
          <path d="M 250 90 Q 350 30, 450 60 T 650 80 T 850 70 T 1050 75" 
                fill="none" 
                stroke="#4a90e2" 
                strokeWidth="1.5"
                strokeOpacity="0.3"
                strokeDasharray="5,5"/>
          
          <g transform="translate(1000, 66)">
            <circle r="32" 
                    fill="none" 
                    stroke="#303030" 
                    strokeWidth="6"/>
            <circle r="32" 
                    fill="none" 
                    stroke="url(#progressGradient)" 
                    strokeWidth="6"
                    strokeDasharray="151 201"
                    filter="drop-shadow(0 2px 3px rgba(0,0,0,0.1))"/>
            <text x="0" y="5" 
                  textAnchor="middle" 
                  fill="#ffffff" 
                  fontSize="16"
                  fontWeight="600">75%</text>
          </g>
          
          <g>
            <circle cx="450" cy="50" r="4" fill="#ff6b6b" opacity="0.8"/>
            <circle cx="650" cy="70" r="4" fill="#2ecc71" opacity="0.8"/>
            <circle cx="850" cy="60" r="4" fill="#4a90e2" opacity="0.8"/>
          </g>
          
          <g>
            <path d="M 300 40 l 10 10 l -10 10" stroke="#ffd93d" fill="none" strokeWidth="2" opacity="0.6"/>
            <path d="M 750 30 l 10 10 l -10 10" stroke="#ff6b6b" fill="none" strokeWidth="2" opacity="0.6"/>
            <circle cx="500" cy="25" r="3" fill="#4a90e2" opacity="0.4"/>
            <circle cx="900" cy="35" r="3" fill="#2ecc71" opacity="0.4"/>
          </g>
          
          <text x="576" y="35" 
                textAnchor="middle" 
                fill="#ffffff" 
                fontSize="22" 
                fontWeight="bold"
                filter="drop-shadow(0 1px 1px rgba(0,0,0,0.1))">
            知识卡学习系统
          </text>
          
          <text x="576" y="115" 
                textAnchor="middle" 
                fill="#999" 
                fontSize="14"
                letterSpacing="1">
            基于认知科学和记忆曲线的智能学习体系
          </text>
        </svg>
      </div>


      {/* 下方左右分栏布局 */}
      <Layout className={styles.studyLayout}>
        <Sider width={360} className={styles.studySider}>
          <Menu
            mode="inline"
            selectedKeys={[selectedCategory?.code]}
            items={menuItems}
            onClick={({ key }) => {
              const category = categories.find(c => c.code === key);
              handleCategoryClick(category);
            }}
            className={styles.categoryMenu}
          />
        </Sider>
        
        <Content className={`${styles.studyContent} Study_studyContent`}>
          {/* 添加专业描述区域到这里 */}
          {selectedCategory && (
            <div className={styles.categoryDescription}>
              <Text className={styles.categoryDescriptionText}>
                {categoryDescriptions[selectedCategory.code]}
              </Text>
            </div>
          )}
          
          {selectedCategory && (
            <div className={styles.categoryContainer}>
              <Row gutter={[24, 24]}>
                {(subCategories[selectedCategory.code] || []).map((subCategory) => {
                  console.log('Rendering subCategory:', subCategory); // 调试用
                  return (
                    <Col xs={24} sm={12} md={8} key={subCategory.code}>
                      <Card 
                        hoverable
                        className={styles.subCategoryCard}
                        onClick={() => handleSubCategoryClick(subCategory)}
                      >
                        <BookOutlined className={styles.cardIcon} />
                        <Title level={4}>{subCategory.name}</Title>
                        <Text type="secondary">{subCategory.description}</Text>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </div>
          )}
        </Content>
      </Layout>
    </div>
  );
};

export default Study; 